import axios from 'axios';
import { API_SUBSCRIPTION_KEY, BASE_API_URLS } from '../config/appConfig';
import { UserPermissions } from '../interfaces/User';
import apiRequest from './apiService';

export interface InviteDetails {
  company_name: string;
  inviter_name: string;
  email: string;
  expires_at: string;
}

export interface RosterMember {
  entry_type: 'member';
  id: string;
  name: string;
  email: string;
  permissions: UserPermissions;
  joined_at: string;
  roster_status: 'accepted';
}

export interface RosterInvite {
  entry_type: 'invite';
  id: string;
  email: string;
  invited_by_name: string;
  created_at: string;
  expires_at: string;
  roster_status: 'pending' | 'expired';
  permissions?: UserPermissions;
}

export type RosterEntry = RosterMember | RosterInvite;

const getInvite = async (token: string): Promise<InviteDetails> => {
  const baseURL = BASE_API_URLS['admin'];
  const response = await axios.get(`${baseURL}/user/get_invite/${token}`, {
    headers: {
      'Content-Type': 'application/json',
      'Ocp-Apim-Subscription-Key': API_SUBSCRIPTION_KEY['admin'],
    },
  });
  return response.data.message;
};

const inviteUser = async (
  email: string,
  permissions?: UserPermissions
): Promise<{ id: string; email: string; expires_at: string }> => {
  const body: { email: string; permissions?: UserPermissions } = { email };
  if (permissions) {
    body.permissions = permissions;
  }
  const result = await apiRequest('admin', 'POST', '/user/invite_user', body);
  return result.message;
};

export interface BulkInviteSucceededItem {
  id: string;
  email: string;
  expires_at: string;
}

export interface BulkInviteFailedItem {
  email: string;
  reason: string;
}

export interface BulkInviteAlreadyInvitedItem {
  email: string;
}

export interface BulkInviteResult {
  succeeded: BulkInviteSucceededItem[];
  failed: BulkInviteFailedItem[];
  already_invited: BulkInviteAlreadyInvitedItem[];
}

// Sending is one synchronous Cosmos-write + ACS-email-send per address on the
// backend, so a single request covering the whole (up to 500-email) batch can
// run far longer than the default 10s client timeout — the client then gives
// up and reports failure while the backend keeps sending for real. Splitting
// into small chunks keeps each request's duration bounded by chunk size
// rather than total batch size; CHUNK_TIMEOUT_MS gives each chunk headroom
// beyond the default timeout as a second line of defense.
const CHUNK_SIZE = 25;
const MAX_CONCURRENT_CHUNKS = 3;
const CHUNK_TIMEOUT_MS = 30000;

const chunk = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

const sendInviteChunk = async (
  emailChunk: string[],
  permissions?: UserPermissions
): Promise<BulkInviteResult> => {
  const body: { emails: string[]; permissions?: UserPermissions } = {
    emails: emailChunk,
  };
  if (permissions) {
    body.permissions = permissions;
  }
  try {
    const result = await apiRequest(
      'admin',
      'POST',
      '/user/invite_users',
      body,
      null,
      CHUNK_TIMEOUT_MS
    );
    return result.message;
  } catch (err: unknown) {
    // A failed chunk (timeout, network error, etc.) must not lose or block
    // the other chunks' results — report just this chunk's emails as failed
    // so the admin can see exactly which addresses need a retry.
    const reason = err instanceof Error ? err.message : 'Request failed';
    return {
      succeeded: [],
      failed: emailChunk.map(email => ({ email, reason })),
      already_invited: [],
    };
  }
};

const inviteUsers = async (
  emails: string[],
  permissions?: UserPermissions
): Promise<BulkInviteResult> => {
  const merged: BulkInviteResult = {
    succeeded: [],
    failed: [],
    already_invited: [],
  };
  const chunks = chunk(emails, CHUNK_SIZE);

  for (let i = 0; i < chunks.length; i += MAX_CONCURRENT_CHUNKS) {
    const wave = chunks.slice(i, i + MAX_CONCURRENT_CHUNKS);
    const waveResults = await Promise.all(
      wave.map(emailChunk => sendInviteChunk(emailChunk, permissions))
    );
    waveResults.forEach(result => {
      merged.succeeded.push(...result.succeeded);
      merged.failed.push(...result.failed);
      merged.already_invited.push(...result.already_invited);
    });
  }

  return merged;
};

interface BackendUser {
  id: string;
  name: string;
  email: string;
  permissions: UserPermissions;
  joined_at?: string;
  created_at: string;
}

const getUsers = async (): Promise<RosterMember[]> => {
  const result = await apiRequest('admin', 'GET', '/user/get_company_users');
  return (result.message as BackendUser[]).map(user => ({
    entry_type: 'member',
    id: user.id,
    name: user.name,
    email: user.email,
    permissions: user.permissions,
    joined_at: user.joined_at ?? user.created_at,
    roster_status: 'accepted',
  }));
};

interface BackendInvite {
  id: string;
  email: string;
  status: 'pending' | 'expired';
  created_at: string;
  expires_at: string;
  permissions?: UserPermissions;
}

const getInvites = async (): Promise<RosterInvite[]> => {
  const result = await apiRequest('admin', 'GET', '/user/get_invites');
  return (result.message as BackendInvite[]).map(invite => ({
    entry_type: 'invite',
    id: invite.id,
    email: invite.email,
    invited_by_name: '',
    created_at: invite.created_at,
    expires_at: invite.expires_at,
    roster_status: invite.status,
    permissions: invite.permissions,
  }));
};

const resendInvite = async (
  id: string
): Promise<{ id: string; email: string; expires_at: string }> => {
  const result = await apiRequest('admin', 'POST', `/user/resend_invite/${id}`);
  return result.message;
};

const revokeInvite = async (id: string): Promise<void> => {
  await apiRequest('admin', 'DELETE', `/user/revoke_invite/${id}`);
};

const deleteInvite = async (id: string, companyId: string): Promise<void> => {
  await apiRequest(
    'admin',
    'DELETE',
    `/user_invite/delete_object/${id}`,
    null,
    {
      company_id: companyId,
    }
  );
};

export {
  getInvite,
  inviteUser,
  inviteUsers,
  getUsers,
  getInvites,
  resendInvite,
  revokeInvite,
  deleteInvite,
};
